import { useState } from "react";
import { signTextColor, type SignPreviewModel } from "../../advertise/creativeModel";
import { isSameOriginApiImage } from "../../advertise/media";

/**
 * A live, approximate preview of how a design reads in town (docs/01 sections 2 and 5): a
 * storefront sign (logo or monogram + business name on the brand colour, accent band) or a wide
 * billboard. Images are contain-fit on a neutral pad and never recoloured; a missing or broken
 * image falls back to the monogram, as the renderer does. Only same-origin API images are shown.
 */
export function SignPreview({ model, size = "md", caption = true }: { model: SignPreviewModel; size?: "sm" | "md"; caption?: boolean }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const url = model.imageUrl && isSameOriginApiImage(model.imageUrl) && failedUrl !== model.imageUrl ? model.imageUrl : null;
  const billboard = model.kind === "billboard";
  return (
    <figure className={`sign-preview sign-preview-${billboard ? "billboard" : "storefront"} sign-preview-${size}`} data-testid="sign-preview" data-mode={url ? "image" : "text"}>
      <div
        className="sign-preview-board"
        role="img"
        aria-label={model.label}
        style={{ background: model.background, color: model.text, borderBottomColor: model.accent }}
      >
        {billboard && url ? (
          <img className="sign-preview-billboard-img" src={url} alt="" draggable={false} onError={() => setFailedUrl(url)} />
        ) : (
          <>
            <span className="sign-preview-mark" style={url ? undefined : { background: model.accent, color: signTextColor(model.accent) }}>
              {url ? <img src={url} alt="" draggable={false} onError={() => setFailedUrl(url)} /> : <span aria-hidden="true">{model.monogram}</span>}
            </span>
            <span className="sign-preview-title">{model.title}</span>
          </>
        )}
      </div>
      {caption ? (
        <figcaption className="small muted">
          {model.host ? (
            <>
              Website link shown to visitors: <strong className="mono">{model.host}</strong>
            </>
          ) : (
            "No website link."
          )}{" "}
          Preview only: the town draws the final sign at its own size.
        </figcaption>
      ) : null}
    </figure>
  );
}

